import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import RefreshIcon from '@material-ui/icons/Refresh'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import Portlet from 'components/reusable/portlet'
import Connect from 'app/redux/connect'
import { Connected } from 'app/interface/redux'
import { businessManagerService } from 'app/service/business-manager'
import {
  AuthorizerConnectionsByUser,
  AuthorizerTokenStatus,
  BusinessError,
} from '@marketing-milk/interfaces'

const BUSINESSES_PER_PAGE = 25

interface BusinessRow {
  business_id: number
  business_name: string
  has_ad: boolean
  has_page: boolean
  errors: BusinessError[]
}

const AD_ONLY_ERRORS: BusinessError[] = [
  BusinessError.ad_account_access_error,
  BusinessError.offline_upload_error,
]

function filterRelevantErrors(errors: BusinessError[], has_ad: boolean): BusinessError[] {
  return errors.filter(e => !AD_ONLY_ERRORS.includes(e) || has_ad)
}

function toBusinessRows(user: AuthorizerConnectionsByUser): BusinessRow[] {
  const map = new Map<number, BusinessRow>()
  for (const conn of user.connections) {
    const existing = map.get(conn.business_id)
    if (existing) {
      existing.has_ad = existing.has_ad || conn.connection_type === 'fb_ad'
      existing.has_page = existing.has_page || conn.connection_type === 'fb_page'
      existing.errors = Array.from(new Set([...existing.errors, ...(conn.errors ?? [])]))
    } else {
      map.set(conn.business_id, {
        business_id: conn.business_id,
        business_name: conn.business_name,
        has_ad: conn.connection_type === 'fb_ad',
        has_page: conn.connection_type === 'fb_page',
        errors: conn.errors ? [...conn.errors] : [],
      })
    }
  }
  return Array.from(map.values()).map(row => ({
    ...row,
    errors: filterRelevantErrors(row.errors, row.has_ad),
  }))
}

function AuthorizerRow({
  authorizer,
  searchQuery,
  currentUserId,
  errorsOnly,
  tokenStatus,
  tokenStatusLoading,
  onErrorsCleared,
}: {
  authorizer: AuthorizerConnectionsByUser
  searchQuery: string
  currentUserId: number
  errorsOnly: boolean
  tokenStatus: AuthorizerTokenStatus | undefined
  tokenStatusLoading: boolean
  onErrorsCleared: (userID: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [bizPage, setBizPage] = useState(1)

  // Reset biz page when search changes
  useEffect(() => {
    setBizPage(1)
  }, [searchQuery, errorsOnly])

  const allRows = useMemo(() => toBusinessRows(authorizer), [authorizer])

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    let rows = allRows
    if (errorsOnly) rows = rows.filter(r => r.errors.length > 0)
    if (!q) return rows
    return rows.filter(r => r.business_name.toLowerCase().includes(q))
  }, [allRows, searchQuery, errorsOnly])

  const totalPages = Math.ceil(filteredRows.length / BUSINESSES_PER_PAGE)
  const pageRows = filteredRows.slice(
    (bizPage - 1) * BUSINESSES_PER_PAGE,
    bizPage * BUSINESSES_PER_PAGE
  )
  const errorCount = filteredRows.filter(r => r.errors.length > 0).length
  const [clearingErrors, setClearingErrors] = useState(false)

  async function handleClearErrors(e: React.MouseEvent) {
    e.stopPropagation()
    setClearingErrors(true)
    try {
      await businessManagerService.clearErrorsForAuthorizer(authorizer.user_id)
      onErrorsCleared(authorizer.user_id)
    } finally {
      setClearingErrors(false)
    }
  }

  // Auto-open when search narrows results
  useEffect(() => {
    if (searchQuery.trim() && filteredRows.length > 0) setOpen(true)
  }, [searchQuery, filteredRows.length])

  if (filteredRows.length === 0 && searchQuery.trim()) return null

  return (
    <div
      style={{ marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          background: open ? '#fafafa' : '#fff',
          userSelect: 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <IconButton
          size="small"
          style={{ padding: 2 }}
          onClick={e => {
            e.stopPropagation()
            setOpen(v => !v)
          }}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
        {authorizer.user_profile_pic ? (
          <img
            src={authorizer.user_profile_pic}
            alt={authorizer.user_name}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              flexShrink: 0,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: '#EEF4FF',
              color: '#1877F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {authorizer.user_name?.[0]?.toUpperCase() ?? '?'}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, lineHeight: 1.2 }}>
            {authorizer.user_name || '—'}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="textSecondary">
              {authorizer.user_email}
            </Typography>
            {authorizer.user_id === currentUserId && (
              <Link
                href="/admin/preferences/account"
                variant="caption"
                style={{ fontWeight: 600 }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                Refresh your token →
              </Link>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {tokenStatusLoading ? (
            <Chip
              icon={<CircularProgress size={10} style={{ marginLeft: 4 }} />}
              label="Checking token..."
              size="small"
              style={{ background: '#f5f5f5', color: '#9e9e9e' }}
            />
          ) : tokenStatus !== undefined ? (
            <Tooltip title={tokenStatus.is_valid ? 'Token valid' : 'Token invalid or expired'}>
              <Chip
                icon={
                  tokenStatus.is_valid ? (
                    <CheckCircleIcon style={{ fontSize: 13, color: '#16a34a' }} />
                  ) : (
                    <ErrorIcon style={{ fontSize: 13 }} />
                  )
                }
                label={tokenStatus.is_valid ? 'Token valid' : 'Token invalid'}
                size="small"
                style={
                  tokenStatus.is_valid
                    ? { background: '#DCFCE7', color: '#15803d' }
                    : { background: '#FEE2E2', color: '#B91C1C' }
                }
              />
            </Tooltip>
          ) : null}
          <Chip
            label={`${allRows.length} business${allRows.length !== 1 ? 'es' : ''}`}
            size="small"
          />
          {errorCount > 0 && (
            <>
              <Chip
                icon={<WarningIcon style={{ fontSize: 13 }} />}
                label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
                size="small"
                style={{ background: '#FEE2E2', color: '#B91C1C' }}
              />
              <Tooltip title="Attempt to clear all errors for this user">
                <span onClick={e => e.stopPropagation()}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      clearingErrors ? (
                        <CircularProgress size={12} />
                      ) : (
                        <RefreshIcon style={{ fontSize: 14 }} />
                      )
                    }
                    disabled={clearingErrors}
                    onClick={handleClearErrors}
                    style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
                  >
                    Clear errors
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <Collapse in={open} unmountOnExit>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f5f5f5' }}>
                <TableCell>Business</TableCell>
                <TableCell>Connections</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={row.business_id}>
                  <TableCell>
                    <Link
                      href={`/admin/business/beta/accounts/edit/${row.business_id}`}
                      variant="body2"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {row.business_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {row.has_ad && (
                        <Chip
                          label="Ad Account"
                          size="small"
                          style={{
                            height: 18,
                            fontSize: 11,
                            background: '#EEF4FF',
                            color: '#1877F2',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {row.has_page && (
                        <Chip
                          label="Page"
                          size="small"
                          style={{
                            height: 18,
                            fontSize: 11,
                            background: '#F0F9FF',
                            color: '#0284C7',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.errors.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
                        <Typography variant="caption" style={{ color: '#757575' }}>
                          Connected
                        </Typography>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {row.errors.map(err => (
                          <div key={err} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <WarningIcon style={{ fontSize: 15, color: '#f44336' }} />
                            <Typography variant="caption" style={{ color: '#f44336' }}>
                              {err === BusinessError.ad_account_access_error
                                ? 'Ad account access error'
                                : 'Offline upload error'}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '6px 12px',
              borderTop: '1px solid #e0e0e0',
            }}
            onClick={e => e.stopPropagation()}
          >
            <Typography variant="caption" color="textSecondary">
              {(bizPage - 1) * BUSINESSES_PER_PAGE + 1}–
              {Math.min(bizPage * BUSINESSES_PER_PAGE, filteredRows.length)} of{' '}
              {filteredRows.length}
            </Typography>
            <Tooltip title="Previous">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setBizPage(p => p - 1)}
                  disabled={bizPage <= 1}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setBizPage(p => p + 1)}
                  disabled={bizPage >= totalPages}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </Collapse>
    </div>
  )
}

function AuthorizersPageComponent(props: Connected) {
  const currentUserId = props.core.session.user!.id
  const [data, setData] = useState<AuthorizerConnectionsByUser[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [tokenStatuses, setTokenStatuses] = useState<Map<number, AuthorizerTokenStatus>>(new Map())
  const [tokenStatusesLoading, setTokenStatusesLoading] = useState(true)

  useEffect(() => {
    businessManagerService
      .getAllAuthorizations()
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    businessManagerService
      .getAuthorizerTokenStatuses()
      .then(statuses => {
        setTokenStatuses(new Map(statuses.map(s => [s.user_id, s])))
      })
      .catch(() => {})
      .finally(() => setTokenStatusesLoading(false))
  }, [])

  function handleErrorsCleared(userID: number) {
    businessManagerService
      .getAllAuthorizations()
      .then(setData)
      .catch(() => {})
  }

  const visibleAuthorizers = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase().trim()
    let list = data
    if (errorsOnly)
      list = list.filter(u => u.connections.some(c => c.errors && c.errors.length > 0))
    if (q) {
      list = list.filter(u => {
        const nameMatch =
          u.user_name.toLowerCase().includes(q) || u.user_email.toLowerCase().includes(q)
        const bizMatch = u.connections.some(c => c.business_name.toLowerCase().includes(q))
        return nameMatch || bizMatch
      })
    }
    return [...list].sort(
      (a, b) =>
        new Set(b.connections.map(c => c.business_id)).size -
        new Set(a.connections.map(c => c.business_id)).size
    )
  }, [data, search, errorsOnly])

  const totalBusinesses = useMemo(
    () => (data ? new Set(data.flatMap(u => u.connections.map(c => c.business_id))).size : 0),
    [data]
  )

  const totalErrors = useMemo(() => {
    if (!data) return 0
    const allRows = data.flatMap(u => toBusinessRows(u))
    const unique = new Map<number, BusinessRow>()
    for (const row of allRows) {
      const existing = unique.get(row.business_id)
      if (!existing || row.errors.length > existing.errors.length) unique.set(row.business_id, row)
    }
    return Array.from(unique.values()).filter(r => r.errors.length > 0).length
  }, [data])

  return (
    <Portlet
      title="Facebook Authorizers"
      headTools={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          {data && (
            <Typography variant="body2" color="textSecondary">
              {data.length} authorizer{data.length !== 1 ? 's' : ''} · {totalBusinesses} business
              {totalBusinesses !== 1 ? 'es' : ''}
              {totalErrors > 0 && (
                <>
                  {' · '}
                  <span style={{ color: '#B91C1C', fontWeight: 600 }}>
                    {totalErrors} error{totalErrors !== 1 ? 's' : ''}
                  </span>
                </>
              )}
            </Typography>
          )}
          <Chip
            icon={<WarningIcon style={{ fontSize: 14 }} />}
            label="Errors only"
            size="small"
            clickable
            onClick={() => setErrorsOnly(v => !v)}
            style={
              errorsOnly ? { background: '#FEE2E2', color: '#B91C1C', fontWeight: 600 } : undefined
            }
          />
          <TextField
            placeholder="Search authorizer or business..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            style={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <CircularProgress />
        </div>
      ) : !data || visibleAuthorizers.length === 0 ? (
        <Typography color="textSecondary" style={{ padding: 16 }}>
          {search ? 'No results match your search.' : 'No authorizers configured.'}
        </Typography>
      ) : (
        <div>
          {visibleAuthorizers.map(authorizer => (
            <AuthorizerRow
              key={authorizer.user_id}
              authorizer={authorizer}
              searchQuery={search}
              currentUserId={currentUserId}
              errorsOnly={errorsOnly}
              tokenStatus={tokenStatuses.get(authorizer.user_id)}
              tokenStatusLoading={tokenStatusesLoading}
              onErrorsCleared={handleErrorsCleared}
            />
          ))}
        </div>
      )}
    </Portlet>
  )
}

export const AuthorizersPage = Connect(AuthorizersPageComponent)
